import './clients.scss'

import { useMachine } from '@xstate/react'
import { useEffect } from 'react'

import { MasterDetail, Section } from '@/components/core-v2'
import { TENANT } from '@/const'

import { BusinessUnitDetailsAndUsers } from '../business-unit-and-users/business-unit-and-users'
import { ClientDetailsAndUsers } from '../client-details-and-users/client-details-and-users'
import { useClientsLayout } from '../clients-layout-provider'
import { ClientsPanel } from '../clients-panel/clients-panel'
import { ClientsMachine, ClientsState, CurrentUserType } from './clients.state'
import { DfmsMachine, DfmsState } from './dfm.state'

export type ClientsStatelessProps = {
  onAddClientComplete: () => void
  onBusinessUnitSaved: () => void
  onClientSaved: () => void
  onClientSearch: (searchTerm: string) => void
  onExistingBusinessUnitSaved: () => void
  onSelectBusinessUnit: (businessUnitId: string) => void
  onSelectClient: (clientId: string) => void
  resetSavedIndicator: () => void
  state: ClientsState
  stateMatches: (val: string) => boolean
  userType: CurrentUserType
}

export type DfmsStatelessProps = {
  onAddDfmComplete: () => void
  onDfmSaved: () => void
  onDfmSearch: (searchTerm: string) => void
  onSelectDfm: (dfmId: string) => void
  resetSavedIndicator: () => void
  state: DfmsState
  stateMatches: (val: string) => boolean
}

export function ClientsStateless({
  onAddClientComplete,
  onBusinessUnitSaved,
  onClientSaved,
  onClientSearch,
  onExistingBusinessUnitSaved,
  onSelectBusinessUnit,
  onSelectClient,
  resetSavedIndicator,
  state,
  stateMatches,
  userType
}: ClientsStatelessProps) {
  return (
    <MasterDetail
      detailChildren={
        <Section data-private type="quaternary">
          {stateMatches('clientSelected') && (
            <ClientDetailsAndUsers
              resetSavedIndicator={resetSavedIndicator}
              state={state}
              userType={userType}
              onBusinessUnitSaved={onBusinessUnitSaved}
              onClientSaved={onClientSaved}
            />
          )}
          {stateMatches('businessUnitSelected') && (
            <BusinessUnitDetailsAndUsers
              resetSavedIndicator={resetSavedIndicator}
              state={state}
              onExistingBusinessUnitSaved={onExistingBusinessUnitSaved}
            />
          )}
        </Section>
      }
      masterChildren={
        <Section data-private type="primary">
          <ClientsPanel
            state={state}
            userType={userType}
            onAddClientComplete={onAddClientComplete}
            onClientSearch={onClientSearch}
            onSelectBusinessUnit={onSelectBusinessUnit}
            onSelectClient={onSelectClient}
          />
        </Section>
      }
      width="narrow"
    />
  )
}

export function DfmsStateless({
  onAddDfmComplete,
  onDfmSaved,
  onDfmSearch,
  onSelectDfm,
  resetSavedIndicator,
  state,
  stateMatches
}: DfmsStatelessProps) {
  return (
    <MasterDetail
      detailChildren={
        <Section data-private type="quaternary">
          {stateMatches('dfmSelected') && (
            <ClientDetailsAndUsers
              resetSavedIndicator={resetSavedIndicator}
              state={state}
              userType="admin"
              onClientSaved={onDfmSaved}
            />
          )}
        </Section>
      }
      masterChildren={
        <Section data-private type="primary">
          <ClientsPanel
            state={state}
            userType="admin"
            onAddClientComplete={onAddDfmComplete}
            onClientSearch={onDfmSearch}
            onSelectClient={onSelectDfm}
          />
        </Section>
      }
      width="narrow"
    />
  )
}

export function ClientsWithState() {
  const [currentClients, sendClients] = useMachine(ClientsMachine)
  const [currentDfms, sendDfms] = useMachine(DfmsMachine)

  const { isPortfolioManagerScreen, userType } = useClientsLayout()

  useEffect(() => {
    if (isPortfolioManagerScreen) {
      sendDfms({ type: 'FETCH_DFMS', tenant: TENANT })
    } else {
      sendClients({ type: 'FETCH_CLIENTS', tenant: TENANT })
    }
  })

  return isPortfolioManagerScreen ? (
    <DfmsStateless
      resetSavedIndicator={() => sendDfms({ type: 'RESET_SAVED_INDICATOR' })}
      state={currentDfms.context}
      stateMatches={(val: string) => currentDfms.matches(val)}
      onAddDfmComplete={() => sendDfms({ type: 'ADD_DFM_COMPLETE' })}
      onDfmSaved={() => sendDfms({ type: 'DFM_SAVED' })}
      onDfmSearch={searchTerm => sendDfms({ type: 'SEARCH_DFMS', searchTerm })}
      onSelectDfm={dfmId => sendDfms({ type: 'SELECT_DFM', dfmId })}
    />
  ) : (
    <ClientsStateless
      resetSavedIndicator={() => sendClients({ type: 'RESET_SAVED_INDICATOR' })}
      state={currentClients.context}
      stateMatches={(val: string) => currentClients.matches(val)}
      userType={userType}
      onAddClientComplete={() => sendClients({ type: 'ADD_CLIENT_COMPLETE' })}
      onBusinessUnitSaved={() => sendClients({ type: 'NEW_BUSINESS_UNIT_SAVED' })}
      onClientSaved={() => sendClients({ type: 'CLIENT_SAVED' })}
      onClientSearch={searchTerm => sendClients({ type: 'SEARCH_CLIENTS', searchTerm })}
      onExistingBusinessUnitSaved={() => sendClients({ type: 'EXISTING_BUSINESS_UNIT_SAVED' })}
      onSelectBusinessUnit={businessUnitId =>
        sendClients({
          type: 'SELECT_BUSINESS_UNIT',
          businessUnitId,
          clientId: currentClients.context?.selectedClient?.clientId || ''
        })
      }
      onSelectClient={clientId => sendClients({ type: 'SELECT_CLIENT', clientId })}
    />
  )
}
